import React, { useState } from 'react';
import { 
  DollarSign, 
  Users, 
  Shield, 
  AlertTriangle,
  Building2,
  Car,
  Package,
  Clock,
  AlertCircle,
  ChevronRight,
  FileText,
  UserCheck,
  Lock,
  TrendingUp,
  MapPin,
  ShieldCheck
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

const EscrowDashboard = () => {
  // Breadcrumb Management
  useBreadcrumbs([
        { label: 'Escrow Management', href: '#' },
        { label: 'Escrow Dashboard' }
      ]);
  const [selectedPeriod, setSelectedPeriod] = useState('today');

  // Enhanced mock data
  const metrics = {
    totalTransactions: 1245,
    activeEscrows: 456,
    totalUsers: 3890,
    disputeRate: 2.3,
    totalVolume: 2500000,
    revenueCollected: 125000,
    successRate: 98.5,
    disputeResolutionRate: 95.2,
    pendingVerifications: 45,
    properties: 234,
    vehicles: 189,
    general: 378,
    riskScore: 82
  };

  // Mock trend data
  const trendData = [
    { month: 'Jan', transactions: 980, volume: 2100000 },
    { month: 'Feb', transactions: 1120, volume: 2300000 },
    { month: 'Mar', transactions: 1340, volume: 2700000 },
    { month: 'Apr', transactions: 1245, volume: 2500000 }
  ];

  // Mock geographic data
  const geoDistribution = [
    { region: 'North America', value: 45 },
    { region: 'Europe', value: 30 },
    { region: 'Asia', value: 15 },
    { region: 'Others', value: 10 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Period Selection */}
        <div className="mb-6">
          <select
            className="mt-1 block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Transaction Volume */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DollarSign className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Transaction Volume
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        ${(metrics.totalVolume / 1000000).toFixed(2)}M
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Revenue/Fees */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Revenue Collected
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        ${(metrics.revenueCollected / 1000).toFixed(1)}K
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Success Rate */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ShieldCheck className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Success Rate
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {metrics.successRate}%
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Score */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Risk Score
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {metrics.riskScore}/100
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts and Details Grid */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Transaction Trends */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Transaction Trends</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="transactions" stroke="#8884d8" name="Transactions" />
                  <Line yAxisId="right" type="monotone" dataKey="volume" stroke="#82ca9d" name="Volume ($)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Geographic Distribution */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Geographic Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={geoDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="region" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" name="Percentage" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Three Column Section */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Active Escrows by Type */}
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900">Active Escrows by Type</h3>
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Building2 className="h-5 w-5 text-gray-400" />
                    <span className="ml-3 text-sm text-gray-500">Properties</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">{metrics.properties}</span>
                    <div className="w-24 h-2 bg-gray-200 rounded-full">
                      <div className="h-2 bg-blue-500 rounded-full" style={{ width: `${(metrics.properties / metrics.activeEscrows) * 100}%` }}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Car className="h-5 w-5 text-gray-400" />
                    <span className="ml-3 text-sm text-gray-500">Vehicles</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">{metrics.vehicles}</span>
                    <div className="w-24 h-2 bg-gray-200 rounded-full">
                      <div className="h-2 bg-green-500 rounded-full" style={{ width: `${(metrics.vehicles / metrics.activeEscrows) * 100}%` }}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Package className="h-5 w-5 text-gray-400" />
                    <span className="ml-3 text-sm text-gray-500">General</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">{metrics.general}</span>
                    <div className="w-24 h-2 bg-gray-200 rounded-full">
                      <div className="h-2 bg-yellow-500 rounded-full" style={{ width: `${(metrics.general / metrics.activeEscrows) * 100}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
              <div className="mt-6 space-y-4">
                <button className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  <div className="flex items-center">
                    <UserCheck className="h-5 w-5 text-gray-400" />
                    <span className="ml-3">Verify Users</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
                <button className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-400" />
                    <span className="ml-3">Review Documents</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
                <button className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-gray-400" />
                    <span className="ml-3">Handle Disputes</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
                <button className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  <div className="flex items-center">
                    <Lock className="h-5 w-5 text-gray-400" />
                    <span className="ml-3">Release Funds</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
          </div>

          {/* Pending Verifications */}
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900">Pending Verifications</h3>
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-500">Total Pending</span>
                  <span className="text-lg font-medium text-gray-900">{metrics.pendingVerifications}</span>
                </div>
                <div className="space-y-3">
                  <div className="bg-yellow-50 p-3 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-yellow-400" />
                        <span className="ml-2 text-sm text-yellow-700">Property Documents</span>
                      </div>
                      <span className="text-sm font-medium text-yellow-700">12</span>
                    </div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-blue-400" />
                        <span className="ml-2 text-sm text-blue-700">Vehicle Inspections</span>
                      </div>
                      <span className="text-sm font-medium text-blue-700">8</span>
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-green-400" />
                        <span className="ml-2 text-sm text-green-700">User Verifications</span>
                      </div>
                      <span className="text-sm font-medium text-green-700">25</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resolution Metrics */}
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900">Resolution Metrics</h3>
              <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Dispute Resolution Rate</span>
                    <span className="text-sm font-medium text-gray-900">{metrics.disputeResolutionRate}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full">
                    <div 
                      className="h-2 bg-green-500 rounded-full"
                      style={{ width: `${metrics.disputeResolutionRate}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Transaction Success Rate</span>
                    <span className="text-sm font-medium text-gray-900">{metrics.successRate}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full">
                    <div 
                      className="h-2 bg-blue-500 rounded-full"
                      style={{ width: `${metrics.successRate}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
            </div>
            <div className="divide-y divide-gray-200">
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Shield className="h-5 w-5 text-gray-400" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">New Property Escrow</p>
                      <p className="text-sm text-gray-500">Transaction #12345</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">5 minutes ago</span>
                </div>
              </div>
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <UserCheck className="h-5 w-5 text-gray-400" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">User Verification Completed</p>
                      <p className="text-sm text-gray-500">User ID #789</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">15 minutes ago</span>
                </div>
              </div>
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Lock className="h-5 w-5 text-gray-400" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Funds Released</p>
                      <p className="text-sm text-gray-500">Transaction #12342</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">1 hour ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EscrowDashboard;